/**
 * Serializes an Insights date-range boundary as a UTC-day bound.
 *
 * The date picker deals in whole calendar days (a date has no time or zone),
 * but the boundaries reach the server as `DateTime` instants and every Insights
 * post query filters `sent_at: { $gte: startDate, $lt: endDate }` against
 * UTC-bucketed days. Two constraints follow, one per bound:
 *
 * - The day sent must be the day the user picked, not a zone-shifted neighbour.
 *   Reading the *local* calendar day (`getFullYear`/`getMonth`/`getDate`) and
 *   re-anchoring it with `Date.UTC` drops the browser offset, so a UTC+ midnight
 *   no longer serializes back to the previous day.
 * - The end bound is *exclusive* (`$lt`). Anchoring it to the day's start would
 *   drop the entire final day (`sent_at < 2026-07-31T00:00Z` excludes all of
 *   July 31); anchoring it to end-of-day keeps the selected end day inclusive.
 */
export function toUTCDayStart(date: Date): string {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
  ).toISOString()
}

/** @see toUTCDayStart — end-of-day bound so the end-exclusive filter keeps the selected day. */
export function toUTCDayEnd(date: Date): string {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999,
    ),
  ).toISOString()
}
